import React, { ReactNode } from "react";
import { useField } from "formik";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker, DatePickerProps, FormItem } from "melco-ui";

interface FormikDatePickerProps
  extends Omit<DatePickerProps, "onChange" | "value" | "name"> {
  name: string;
  label?: string;
  info?: ReactNode;
  onChange?: (value: Date | undefined, dayjsValue: Dayjs | null) => void;
  disabledDate?: (currentDate: Dayjs) => boolean;
}

const DatePickerField: React.FC<FormikDatePickerProps> = ({
  name,
  label,
  info,
  onChange,
  disabledDate,
  ...restProps
}) => {
  //const { t } = useTranslation();
  const [field, meta, helpers] = useField(name);

  const hasFeedback = !!(meta.touched && meta.error);
  const help = meta.touched && meta.error ? meta.error : undefined;
  const validateStatus = meta.touched && meta.error ? "error" : undefined;

  const selectedDate = field.value ? dayjs(field.value) : null;

  return (
    <FormItem
      label={label}
      help={hasFeedback ? help : undefined}
      validateStatus={validateStatus}
      hasFeedback={hasFeedback}
      info={info}
    >
      <DatePicker
        {...restProps}
        {...field}
        value={selectedDate}
        format="L"
        name={name}
        disabledDate={disabledDate}
        className={restProps.className || "full-width-dropdown"}
        style={{ width: "100%", ...(restProps.style || {}) }}
        onChange={(value) => {
          const date: Date | undefined = value
            ? value.startOf("day").toDate()
            : undefined;
          helpers.setValue(date);

          if (onChange) {
            onChange(date, value);
          }
        }}
        onBlur={() => helpers.setTouched(true)}
      />
    </FormItem>
  );
};

export { DatePickerField };

/**
 * Example Use Case:
 * <Formik initialValues={{ startDate: undefined }} onSubmit={...}>
 *   <Form>
 *     <DatePickerField
 *       name="startDate"
 *       label="Start Date"
 *       disabledDate={(current) => current && current < dayjs().startOf("day")}
 *       info="Please select a future date"
 *     />
 *   </Form>
 * </Formik>
 */
